import { supabase } from '@/lib/supabase/client'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { Canvg } from 'canvg'
import { PDFDocument, PDFPage } from 'pdf-lib'
import type { MTReportData } from '@/types/mtTypes'
import { getNdtResultBodyStyles, getNdtResultTableOptions } from './pdfTitle'
import { resolveSignatureUrl } from '@/utils/signatureUrl'
import { findProfileByName } from './ndtPdfShared'

// Define the ReportData interface
interface ReportData {
  testDate: string
  reportNumber: string
  objects: string[]
  objectDescription: string
  issuedBy: string
  status: 'Accepted' | 'Not Accepted'
  company?: string
  company_id?: string
  certificationNumber: string
  sequenceNumber: string
  drawing?: string
  diameter?: string
  thickness?: string
  // MT-specific fields
  magnetizingEquipment: string
  lighting: string
  demagnetization: string
  otherEquipment: string
  current: string
  fieldStrength: string
  magneticInk: string
  inkBatchNo: string
  contrast: string
  contrastBatchNo: string
  extentOfExamination: string
  comments?: string
  mpiType?: 'fluorescent' | 'standard'
}

// Helper function to format date in DD.MM.YYYY format
function formatDateDDMMYYYY(dateString: string): string {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

// SVG for STM logo
const STM_LOGO_SVG = `<svg width="12662" height="3631" viewBox="0 0 12662 3631" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M3824.18 2586.89C3698.69 2586.89 3590.68 2568 3500.16 2530.22C3409.64 2492.44 3326.32 2443.54 3250.2 2383.54L3105.16 2543.55H3012.59V1843.49H3108.25C3132.94 1952.39 3173.05 2054.62 3228.6 2150.18C3286.2 2245.75 3363.35 2323.53 3460.04 2383.54C3556.73 2441.32 3675.03 2470.21 3814.92 2470.21C3930.13 2470.21 4025.79 2450.21 4101.91 2410.21C4180.09 2370.2 4238.72 2315.75 4277.81 2246.86C4318.95 2175.74 4339.53 2097.96 4339.53 2013.51C4339.53 1926.83 4321.01 1856.83 4283.98 1803.49C4246.95 1750.15 4198.6 1709.04 4138.94 1680.14C4081.34 1649.03 4018.59 1624.58 3950.7 1606.8C3884.87 1589.02 3822.12 1572.36 3762.46 1556.8C3618.45 1516.8 3491.93 1464.57 3382.89 1400.12C3273.86 1333.45 3188.48 1247.88 3126.76 1143.43C3067.1 1038.98 3037.27 908.967 3037.27 753.398C3037.27 582.272 3071.22 441.149 3139.11 330.028C3207 218.908 3296.49 136.678 3407.58 83.3404C3518.67 27.7801 3636.97 0 3762.46 0C3855.04 0 3937.33 14.4456 4009.33 43.3369C4083.4 72.2283 4161.57 114.454 4243.86 170.015L4364.21 30.0026H4459.88V710.061H4367.3C4307.64 498.932 4227.4 347.807 4126.6 256.689C4025.79 163.347 3902.36 116.677 3756.29 116.677C3667.83 116.677 3586.56 132.233 3512.5 163.347C3440.5 192.239 3382.89 234.464 3339.69 290.025C3296.49 345.585 3274.89 411.146 3274.89 486.708C3274.89 557.825 3293.4 616.719 3330.43 663.39C3367.46 707.838 3415.81 743.397 3475.47 770.066C3535.13 796.735 3598.91 818.959 3666.8 836.738C3734.69 854.518 3799.49 871.186 3861.21 886.743C4017.56 926.746 4149.23 982.306 4256.21 1053.42C4363.18 1122.32 4444.45 1213.44 4499.99 1326.78C4555.54 1440.12 4583.31 1582.36 4583.31 1753.48C4583.31 2015.73 4517.48 2220.19 4385.81 2366.87C4256.21 2513.55 4068.99 2586.89 3824.18 2586.89Z" fill="#3A3A3A"/>
  <path d="M5149.07 2543.55V2443.54H5534.81V130.011H5380.51C5267.36 130.011 5169.64 163.347 5087.35 230.02C5007.12 296.692 4944.37 384.477 4899.11 493.376C4855.91 602.274 4833.28 720.062 4831.22 846.739H4707.79V30.0026H6849.41V846.739H6725.97C6723.91 720.062 6700.25 602.274 6654.99 493.376C6611.79 384.477 6550.07 296.692 6469.84 230.02C6389.6 163.347 6291.88 130.011 6176.68 130.011H6022.38V2443.54H6386.52V2543.55H5149.07Z" fill="#3A3A3A"/>
  <path d="M8104.24 2573.55L7443.85 263.356H7431.51V2443.54H7770.96V2543.55H6956.28V2443.54H7295.73V130.011H6956.28V30.0026H7931.43L8366.54 1546.8H8378.88L8814 30.0026H9708.91V130.011H9387.97V2443.54H9708.91V2543.55H8570.21V2443.54H8900.4V263.356H8888.06L8230.76 2573.55H8104.24Z" fill="#3A3A3A"/>
  <path d="M6331 3631L0 2941.29H12662L6331 3631Z" fill="#FB4B4E"/>
</svg>`;

// Use canvg to render SVG to canvas and get data URL
async function getLogoDataUrl(): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }
  
  // Set canvas size to desired dimensions - higher resolution for better quality
  canvas.width = 400; // Doubled for better quality
  canvas.height = 120; // Doubled for better quality
  
  try {
    // Create a new Canvg instance
    const v = await Canvg.fromString(ctx, STM_LOGO_SVG);
    
    // Scale SVG to fit canvas
    v.resize(400, 120, 'xMidYMid meet');
    
    // Render SVG
    await v.render();
    
    // Get data URL - full quality PNG
    return canvas.toDataURL('image/png');
  } catch (error) {
    return '';
  }
}


// Function to get MT certificate number, MT level, and equipment for a user
async function getInspectorCertData(fullName: string): Promise<{ certificateNo: string | null; mtLevel: number | null; mtEquipment: string | null }> {
  try {
    const { data: inspector, error } = await supabase
      .from('profiles')
      .select('mt_cert, mt_level, mt_equipment')
      .eq('full_name', fullName)
      .single()

    if (error || !inspector) {
      console.log(`No certificate data found for: ${fullName}`);
      return { certificateNo: null, mtLevel: null, mtEquipment: null }
    }

    return {
      certificateNo: inspector.mt_cert || null,
      mtLevel: inspector.mt_level || null,
      mtEquipment: inspector.mt_equipment || null
    };
  } catch (error) {
    console.error('Error fetching certificate data:', error);
    return { certificateNo: null, mtLevel: null, mtEquipment: null }
  }
}

// Function to get MT certificate PDF for a user
async function getMTCertificatePDF(fullName: string): Promise<ArrayBuffer | null> {
  try {
    const { data: inspector, error } = await supabase
      .from('profiles')
      .select('mt_doc')
      .eq('full_name', fullName)
      .single()

    if (error || !inspector?.mt_doc) {
      console.log(`No MT certificate found for: ${fullName}`);
      return null
    }

    const mtDocUrl = inspector.mt_doc;
    console.log('MT document URL:', mtDocUrl);

    // Check if it's a URL (starts with http/https) or a storage path
    if (mtDocUrl.startsWith('http://') || mtDocUrl.startsWith('https://')) {
      // It's a full URL - fetch it directly
      try {
        const response = await fetch(mtDocUrl);
        if (!response.ok) {
          console.error(`Failed to fetch MT certificate from URL: ${response.status} ${response.statusText}`);
          return null;
        }
        return await response.arrayBuffer();
      } catch (fetchError) {
        console.error('Error fetching MT certificate from URL:', fetchError);
        return null;
      }
    } else {
      // It's a storage path - use Supabase storage
      const { data: pdfData, error: downloadError } = await supabase
        .storage
        .from('certificates')
        .download(mtDocUrl)

      if (downloadError) {
        console.error('Error downloading MT certificate from storage:', downloadError);
        return null
      }

      // Convert blob to ArrayBuffer
      return await pdfData.arrayBuffer()
    }
  } catch (error) {
    console.error('Error fetching MT certificate:', error);
    return null
  }
}

// Function to get signature for a user
async function getSignature(fullName: string): Promise<string | null> {
  const result = await getSignatureInternal(fullName);
  // Ensure we always return a string (data URL) or null, never ArrayBuffer
  if (typeof result === 'string') {
    return result;
  }
  return null;
}

// Internal signature retrieval function
async function getSignatureInternal(fullName: string): Promise<string | null> {
  try {
    // Get signature URL from profiles table first, then try inspectors table
    const profile = await findProfileByName<{
      signature: string | null
      inspectors: { signature?: string | null } | { signature?: string | null }[] | null
    }>(fullName, 'signature, inspectors(signature)')

    const inspectors = profile?.inspectors
    const inspectorSignature = Array.isArray(inspectors) ? inspectors[0]?.signature : inspectors?.signature
    const signatureUrl = profile?.signature || inspectorSignature

    if (!signatureUrl) {
      console.log(`No signature found for: ${fullName}`);
      return null
    }

    // Get optimized version of the signature
    const sigImg = await getOptimizedImage(await resolveSignatureUrl(signatureUrl));
    
    // Check if optimization worked
    if (!sigImg) {
      return null
    }
    
    return sigImg;
  } catch (error) {
    return null
  }
}

// New function to optimize images - but keep full quality
async function getOptimizedImage(imgUrl: string): Promise<string | null> {
  // Check if the URL is valid
  if (!imgUrl || typeof imgUrl !== 'string' || !imgUrl.trim()) {
    return null
  }
  
  // If it's already a data URL, just return it
  if (imgUrl.startsWith('data:')) {
    return imgUrl
  }
  
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");

    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Keep original dimensions but limit maximum size
      const maxDimension = 200; // Increased from 150 for better quality
      let width = img.width;
      let height = img.height;
      
      if (width > height && width > maxDimension) {
        height = (height * maxDimension) / width;
        width = maxDimension;
      } else if (height > maxDimension) {
        width = (width * maxDimension) / height;
        height = maxDimension;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null)
        return
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      try {
        // Use PNG for best quality
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (err) {
        resolve(null);
      }
    };

    img.onerror = () => {
      // Try with a proxy as a fallback for CORS issues
      if (!imgUrl.includes('cors-anywhere')) {
        const proxyUrl = `https://cors-anywhere.herokuapp.com/${imgUrl}`
        img.src = proxyUrl
      } else {
        resolve(null)
      }
    };

    img.src = imgUrl;
  });
}

// Update getFullName function to just return the name since it's already full
function getFullName(name: string): string {
  return name
}

// Update getExtentOfExamination function for MT
function getExtentOfExamination(reportData: ReportData): string {
  return reportData.extentOfExamination || '---';
}

// Base64 encoded STM logo - using a PNG instead of SVG to avoid compatibility issues
const STM_LOGO_PNG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAZAAAABkCAYAAACoy2Z3AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAApbSURBVHgB7Z3/bxTHFce/O2dTjElCWgkU04JUkARCkNIScKwmlWj/g/Z/7B/b/qIqUmukCESVkBLAQAhxUhuutmdjm917nZnz7Pi8d3vn2/X6Nh/JYrO3Nzs7887nzXs7swYEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQRAEQRAKWEEQBEEQRhDWlxZGIhHJtxYiEUEQBKGFRAIpRiKCIAhCC4kEUoxEBEEQhBYw/j+++G4lDcN+WfzoyiYIgiAIGUYgENv77+r3QfBzPn/n0nWCIAiC0JgRzs9R6ztLi0vBuS+PTM/QBUEQhKnGCSQ+W/nut5/+6Rp3h4IgCMJUM5yAiqEyOoKRiyAIwvQynIBCvWs6JWMPesoVVPZqZE7XkBAEQZguhheQpYKdGDKm1xDXE0UQBEGYQoYX0BBwbJGQiCAIwnQySgERBEEQhGOIgARBEIRaEAEJgiAItRhdQFT87pRRKZ+UfGqZwyIIgjB1jDYCYfGICICq2EPfCyqbJQiCIEwdI05h9RCCMVGiG4IgCFPK6BPrKH3XYjsAGdoQBEGYQkYX0JCJ9JJzKAiCMG2MLiB2nDXMlZ9SkZV++jlOh7BNOoIgCEInML6fO7/aJ9aJbyTgaQWe9nK28lv3/Tt9Jv1c+jt8rvg+zG+xD+S7dMMuaL4N/0/t7dEHEARBEDqB8Wsd0KvjrfX0a//PrfeUW5tEm6f93PJORy31lDdrPVf7LW0r8LQXWKHaX9wKBEEQhE5gfE/f8BZpLCJ6d2jS7W7m+Px+z6G3U/OJpq7yKPSOEPh5ZcXs3pXsv6v78+6Pq+r1z8VNJQiC0A3YKYIL3sHcvEBeXN35m3cwt6H88Kt+5u8ffXu7Zt3cXlx58ILc9+dX59fKn1GvzpwLV+n9+fnh1FBSFVhm/17anV53Zbx/vbRGaR6RKAiILSb4QUUmrK0sRDUPvf6TT/XUXh+OBX1zflXm7N2JwmDI97OmcQe5Ntmd0wQXAj/ngtLWx4sXnCpb0RCgiAI7efnfnnrdgjEw0vbdQl4bE0h2V3fT4QQxc9/eOMaT7PZd2kUoahvnY0L0YdLz1+6kJGQIAhC+xnJSXblPb4xG/cTyoYz6srzm17C9pYXvLlCEjp7Ewrm6xfIKuNDhWcpO7DcUcLb7Pj05mfnXXrEPnR1N50yTW59d0q7/i1+iNDfq3fPbdKKS6hx2G/9/zb6gZH5t+QXOYFR3Z9aYxQVDDOBpHcaqGi/KPbHpJpPtTGjxzxZCyf0Xwn/vqEoeMXmQwqCIAjtp2VR+dxNw9k4MJ7C5gVBEITjw9L9Gzc8e88CkpGIIAhCIwIvBHwHLK8y8X7xnisT6qeZ06JlAuLbdDJdE9kv5JMG9oZCEARh2uHCvS5ewA973ufFe67Ow7srA3GbTbvl0X8fvJ1B5CvRcAYD9cGJq4IgCEJK6qiY78JnLgzEnJaLaOmZS5tptYB8XJ4r0wJphKGiGjzh4SvP+y0oVCCi0SB1w/mxxaJhfCOZDOc4CifdLd3+2f3ZPT3p/tyyC76XrFvkHKz8XoaxLWJUlH9L/i4vomTY2A4raxdbJ6HXxz4LXoPBe5C9Ztlfwe2+tLt/r9RJ3hGZdCk6TnB+hnpyfWs7I6DQIfDPtMoAIQeT0IkQ0AvFe/5FWPDOWFcPXq+CQocKOQnl/g4X73Hx3n4mbS/9vLXfK/8Yd1L5tCYT7a1vlCVHV8Fvce6t/OP8Q7++cDnTSedTnpz86vz6i/v8d1RBGvHdTxJu/+7OFZMR/EkXAFu8nzsdPxGHXr3o5Xt1oZZ16qdvfLpL+TeXtxcofaboEZP79kG6Xvz2QvqMAumxNrA9vLDPpvVpvQ7Pz4s6+SBB2SnYUkrx8Vw3Ku3/MrzuyvfDnx957fSoRJ2Rd+Z6cSfuPJl+dDqOiusjbXNhxeVuOXTw9PjzT/wGUV5A/GYVNtVe3fPOCO7tFvzRu3N7z/7NFQA27e2VOeecTgNh3Df8LrXm3vlTcfJWKbFxoVz5vfhXqb+mPlb1t3gnT/m7BXj5u8X7TF6dZMNzdeJdT0qLmVTwNxj8FhsNsZZt/yPXb1MfcPemPKTtSus0aP/MoHezvfL1GdFBHVzYTnl6k/OgZuLFdrBYZzxfWYqUJZunrSYVEO9iCgIK1sV3ROwk82KqvRCo0Lv6kEsqCXVi1KsRe9OpCFxhyTqrI60f38hJ7Bw5V1f77RHq7Ac0XFz4OLfCWtBkBRRUuEF5U1xnGIGc/M3V0jWd2UliXTGg+PWh/jrT9L9UzCiXBLSrnFv5Qa+D24t3qJIQ1C/1v+D85eLXh7eRQ9Tv0ZWvX/u/YfOTQXhevvPrj5+UlmVKPSEWnTFm8DxbVOAZI0UFuY5xQXRhgU5GQJk0Kgjovq8Mi6d2QgXZIePCH5xMJeNsQOYmCsIPGqzrSKmFQkJC/kwRrtBz++16dFPNg8f1vsjRsL0wz8IzcdFe0Omj/Yf9aW3PmZbXMeQZt2D6eLfTqK2dJi0Sez9BUEVrJHjnqXuiCBeMFgvIe+vOtpNxtJzaSUQTbxQmPegJuNPWrF5wKK/cUdjLhXcCZHPr23VXf/9h7VG+jLr9qAzXjx2NlgrIC8o5VZujwrTzRfnwPDiVXaVVzBhO3XjOfVWnrWlFPpZ9J1Wlw+tEcJk68fOjuPO2+z5ZrR1Uw/66qSi0d3rqg6/n+Jxsl/6NUUfH6sRFpBMZ5XiJgOrOt/H08jh4j/Tq9M1++nrY9i+iVQICF+yk8GNn3+M3nX2R/XRqZxzYI/HrwGJhP3QvhXeD9sGl5+fK2yePAj7HO4m0JZm6Dpp6aSnGjtOppxN1Ysfdz4HqfTr9wkh40YWZ1F43rF/odNJ281mfq1N/F8eSE1CwhBcmTOzEcqGkwjsKmyA3cU5BuuCCPSr8rRbOc7EYCvUZRSjqH1HGcS3sJO5vU19Fae5p6qlKwXsKF5L8+VqJzTu9sZ9NqaRsO4hj/EBlQiF1fLpunXzfpNINt6z5zVvdcyGKQYS6mfr/0r5OlxbupXWbOdLv52P5lGGdDvQhxXPk6+NcRzb/LnV/njr1t1ifC+TFVRXS5oQbLOHF/eBxunbPLnPf8W2cvILQQdOJHUt2gUcmfEgn+KHSezqaWqZRoTp+KGLo8P28oXRBuEPSVVbdG4lrb1Qlv9ihz59X4X3pdDY/b2ycOl1eP24qsN67+79BZKPq/mzPSJ1OHPrW6Rrb/6S9bSQIgiAIbaCqgPjWfneS/6L0ORVXH+yFYvOo9Kbw3pI3P3lRp66CIAjCcSPvhGDxFArAiWWpZHqnrFRkpcx++fdBBUEQhK5ieZ6n9PvnQhAEQRAGKBURz9+cqLBWKQiCIEw31vJ8jyAIgiCMRnV/a0EQBEEoQZadFQRBEARBEARBEARBEARBEARBEARBEARBEARBEARBEARBEAShRfwfRavDjlmwnlUAAAAASUVORK5CYII=';

export async function generatePDF(data: MTReportData): Promise<Blob> {
  try {
    // Log the names being used for signature lookup
    console.log('MT PDF Generation - Looking for signatures:');
    console.log('Issued By:', data.issuedBy);
    const verifierName = (data.approvedBy || data.verifiedBy || '').trim();
    console.log('Approved By:', verifierName || 'None');
    
    // First, get the original inspector's certificate data to check their level
    const [originalInspectorCertData, inspectorSignature, logoDataUrl] = await Promise.all([
      getInspectorCertData(data.issuedBy),
      getSignature(data.issuedBy),
      getLogoDataUrl()
    ]);
    
    // Only use approver's certificate if inspector is Level 1 AND report is approved
    // Level 2+ inspectors keep their own certificate even when approved
    const isLevel1Inspector = originalInspectorCertData.mtLevel === 1;
    const shouldUseApproverCert = isLevel1Inspector && verifierName && verifierName !== data.issuedBy;
    
    // Determine certificate owner based on inspector level
    let certData = originalInspectorCertData;
    let mtCertificatePDF: ArrayBuffer | null = null;
    let approverSignature: string | null = null;
    
    if (shouldUseApproverCert) {
      // Level 1 inspector: use approver's certificate
      const [approverCertData, approverCertPDF, approverSig] = await Promise.all([
        getInspectorCertData(verifierName),
        getMTCertificatePDF(verifierName),
        getSignature(verifierName)
      ]);
      certData = approverCertData;
      mtCertificatePDF = approverCertPDF;
      approverSignature = approverSig;
    } else {
      // Level 2+ inspector: keep inspector's certificate, but get approver's signature if approved
      const [inspectorCertPDF, approverSig] = await Promise.all([
        getMTCertificatePDF(data.issuedBy),
        verifierName ? getSignature(verifierName) : Promise.resolve(null)
      ]);
      mtCertificatePDF = inspectorCertPDF;
      approverSignature = approverSig;
    }
    
    // Extract certificate number, MT level, and equipment
    const { certificateNo: certificationNumber, mtLevel, mtEquipment } = certData;
    
    // Log signature and certificate results
    console.log('Signature results:');
    console.log('Inspector signature found:', !!inspectorSignature);
    console.log('Approver signature found:', !!approverSignature);
    console.log('Certificate number found:', certificationNumber || 'None');
    console.log('MT level found:', mtLevel || 'None');
    console.log('MT equipment found:', mtEquipment || 'None (using default)');
    console.log('MT certificate PDF found:', !!mtCertificatePDF);

    // Create new jsPDF document - exact sizing to match original
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Basic settings - exact matching to original
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;
    
    // HEADER - Title and Logo (styled like UI: blue bold + gray light)
    doc.setFontSize(14);
    
    // First part in blue and bold
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95); // #1E3A5F - navy blue
    const firstPart = data.mpiType === 'fluorescent'
      ? 'Fluorescent Magnetic Particle'
      : 'Magnetic Particle';
    doc.text(firstPart, margin, margin);
    
    // Calculate width of first part to position second part
    const firstPartWidth = doc.getTextWidth(firstPart);
    
    // Second part "Testing" in black and light/normal weight
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(65, 65, 65); // black/dark gray
    doc.text(' Testing', margin + firstPartWidth, margin);
    
    // Add logo with fallback to text if image couldn't be loaded
    if (logoDataUrl && typeof logoDataUrl === 'string') {
      doc.addImage(logoDataUrl as string, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12);
    } else {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('STM INSPECTION AS', pageWidth - margin - 20, margin, { align: 'center' });
    }
    
    // Add more space between header and first table
    let yPos = margin + 15;
    
    // TABLE OPTIONS - matching ET template styling exactly
    const tableOptions = {
      theme: 'plain' as const,
      styles: {
        fontSize: 8,
        cellPadding: 0.5,
        lineColor: [0, 0, 0] as [number, number, number],
        lineWidth: { top: 0, right: 0.01, bottom: 0, left: 0.01 },
        minCellHeight: 1.5
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        textColor: [65, 65, 65] as [number, number, number],
        fontStyle: 'normal' as const,
        fontSize: 6,
        cellPadding: 0.3,
        lineWidth: { top: 0.01, right: 0.01, bottom: 0, left: 0.01 },
        lineColor: [0, 0, 0] as [number, number, number],
        halign: 'left' as const,
        valign: 'middle' as const
      },
      bodyStyles: {
        lineWidth: { top: 0, right: 0.01, bottom: 0, left: 0.01 }
      },
      margin: { left: margin, right: margin },
      tableLineColor: [0, 0, 0] as [number, number, number],
      tableLineWidth: 0.01
    };
    
    // Define standard column widths - matching ET template
    const leftColumnWidth = pageWidth * 0.7 - margin;
    const rightColumnWidth = pageWidth * 0.3 - margin;

    // TEST DATE, LOCATION, REPORT NO - matching ET template
    autoTable(doc, {
      startY: yPos,
      head: [['TEST DATE', 'LOCATION', 'REPORT NO.']],
      body: [[
        formatDateDDMMYYYY(data.testDate),
        data.testSite || 'Bergen',
        `MT-${data.reportNumber ?? 'N/A'}.${data.sequenceNumber || '001'}`
      ]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 2 },
        1: { cellWidth: leftColumnWidth / 2 },
        2: { 
          cellWidth: rightColumnWidth, 
          overflow: 'linebreak',
          halign: 'left',
          valign: 'middle'
        }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // Format object description based on company-specific fields
    const formatObjectDescription = () => {
      const parts: string[] = [data.objectDescription];
      
      // Only add fields if they have actual values
      if (data.customerProject && data.customerProject.trim()) {
        parts.push(`Customer Project: ${data.customerProject}`);
      }
      
      // Only add hull/yard if it has a value (company requires it and user filled it)
      if (data.hullYard !== undefined && data.hullYard !== null && data.hullYard !== '') {
        const hullYardValue = data.hullYard.trim() || 'Ukjent/Unknown';
        parts.push(`Hull/Yard: ${hullYardValue}`);
      }
      
      if (data.charge && data.charge.trim()) {
        parts.push(`Charge: ${data.charge}`);
      }
      
      return parts.join('\n');
    };

    // OBJECT and MATERIAL table
    autoTable(doc, {
      startY: yPos,
      head: [['OBJECT', 'MATERIAL']],
      body: [[formatObjectDescription(), data.material]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth },
        1: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // DRAWING, DIAMETER and THICKNESS table - matching ET template widths
    autoTable(doc, {
      startY: yPos,
      head: [['DRAWING', 'DIAMETER', 'THICKNESS']],
      body: [[data.drawing || '---', data.diameter || '---', data.thickness || 'Varies']],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: (pageWidth - 2*margin) - rightColumnWidth },
        1: { cellWidth: rightColumnWidth / 2 },
        2: { cellWidth: rightColumnWidth / 2 }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // SURFACE CONDITIONS, TEMPERATURE OF OBJECT, COMPANY / OPERATOR CERT NO
    // mt_cert from profiles already contains the full certificate number with level
    const cleanCertNumber = (certificationNumber || '---').replace(/[\r\n\t\s]+/g, '').trim();
    const certNumber = `STMI / ${cleanCertNumber}`;
    autoTable(doc, {
      startY: yPos,
      head: [['SURFACE CONDITIONS', 'TEMPERATURE OF OBJECT', 'COMPANY / OPERATOR CERT NO']],
      body: [[data.surfaceCondition, '0-35°C', certNumber]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 2, overflow: 'linebreak' },
        1: { cellWidth: leftColumnWidth / 2, overflow: 'linebreak' },
        2: { 
          cellWidth: rightColumnWidth, 
          halign: 'left' as const,
          valign: 'middle' as const
        }
      },
      didParseCell: function(data: any) {
        // Force no line breaks in the certification number column
        if (data.column.index === 2) {
          data.cell.styles.halign = 'left'
          data.cell.styles.valign = 'middle'
          // Remove any line break characters and other problematic characters
          if (typeof data.cell.text === 'string') {
            data.cell.text = data.cell.text
              .replace(/\n/g, ' ')
              .replace(/\r/g, ' ')
              .replace(/\t/g, ' ')
              .replace(/\u00A0/g, ' ') // Non-breaking space
              .replace(/\u200B/g, '') // Zero-width space
              .replace(/\u200C/g, '') // Zero-width non-joiner
              .replace(/\u200D/g, '') // Zero-width joiner
              .replace(/\s+/g, ' ') // Multiple spaces to single space
              .trim()
          }
        }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // MAGNETIZING EQUIPMENT USED, LIGHTING, DEMAGNETIZATION
    // If useCoil is checked (Standard MPI), use Coil equipment; otherwise use inspector's stored equipment or default
    const coilEquipment = 'Coil, Western Instruments WDV14KP SN: C429';
    const magnetizingEquipmentUsed = data.useCoil 
      ? coilEquipment 
      : (mtEquipment || data.equipmentSettings.magnetizingEquipment || 'Yoke, Magnaflux Y-1, sn: 2291');
    autoTable(doc, {
      startY: yPos,
      head: [['MAGNETIZING EQUIPMENT USED', 'LIGHTING', 'DEMAGNETIZATION']],
      body: [[
        magnetizingEquipmentUsed,
        data.equipmentSettings.lighting || '1500 uW/cm2 / <20 LUX',
        data.equipmentSettings.demagnetization || 'No'
      ]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 2 },
        1: { cellWidth: leftColumnWidth / 2 },
        2: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // OTHER EQUIPMENT USED, CURRENT, FIELD STRENGTH
    autoTable(doc, {
      startY: yPos,
      head: [['OTHER EQUIPMENT USED', 'CURRENT', 'FIELD STRENGTH']],
      body: [[
        data.equipmentSettings.otherEquipment || 'Castrol Strips Type A (type II / Silver)',
        data.equipmentSettings.current || 'AC / 220v',
        data.equipmentSettings.fieldStrength || '4,75 Kg – 15 cm'
      ]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 2 },
        1: { cellWidth: leftColumnWidth / 2 },
        2: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // MAGNETIC INK, BATCH NO, CONTRAST, BATCH NO/SERIAL NO.
    autoTable(doc, {
      startY: yPos,
      head: [['MAGNETIC INK', 'BATCH NO', 'CONTRAST', 'BATCH NO/SERIAL NO.']],
      body: [[
        data.equipmentSettings.magneticInk || 'Fluxo 6',
        data.equipmentSettings.inkBatchNo || 'L231106-1',
        data.equipmentSettings.contrast || 'UV – Light, 365 Nm',
        data.equipmentSettings.contrastBatchNo || 'M3Zb16332'
      ]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth / 3 },
        1: { cellWidth: leftColumnWidth / 3 },
        2: { cellWidth: leftColumnWidth / 3 },
        3: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // TEST PROCEDURE
    autoTable(doc, {
      startY: yPos,
      head: [['TEST PROCEDURE']],
      body: [[data.testProcedure || 'MT DNV-CG-0051']],
      ...tableOptions
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // Format extent of examination
    const formatExtentOfExamination = () => {
      // Check if it's already "Acc. to spec" or a custom text
      if (data.extentOfExamination === 'Acc. to spec' || isNaN(Number(data.extentOfExamination))) {
        return data.extentOfExamination;
      }
      // Otherwise it's a percentage
      return `${data.extentOfExamination}%`;
    };

    // ACCEPTANCE, EXTENT OF EXAMINATION
    autoTable(doc, {
      startY: yPos,
      head: [['ACCEPTANCE', 'EXTENT OF EXAMINATION']],
      body: [[data.testAcceptance || 'DNV-CG-0051 Level 1', formatExtentOfExamination()]],
      ...tableOptions,
      columnStyles: {
        0: { cellWidth: leftColumnWidth },
        1: { cellWidth: rightColumnWidth }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // REFERENCE TO APPLICABLE RULES AND STANDARDS
    autoTable(doc, {
      startY: yPos,
      head: [['REFERENCE TO APPLICABLE RULES AND STANDARDS']],
      body: [[(data.referenceStandards && data.referenceStandards !== '---') ? data.referenceStandards : 'DNV CG 0051']],
      ...tableOptions
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // Calculate remaining space for summary
    const totalUsableHeight = pageHeight - (2 * margin);
    const spaceForResultAndSignatures = 40;
    const currentY = yPos;
    const remainingSpace = totalUsableHeight - currentY - spaceForResultAndSignatures;
    const summaryHeight = Math.max(remainingSpace, 40);

    // Summary table options - matching ET template exactly
    const summaryTableOptions = {
      theme: 'plain' as const,
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [0, 0, 0] as [number, number, number],
        lineWidth: 0.01,
        minCellHeight: 3
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        textColor: [65, 65, 65] as [number, number, number],
        fontStyle: 'normal' as const,
        fontSize: 6,
        cellPadding: 1,
        lineWidth: { top: 0.01, right: 0.01, bottom: 0, left: 0.01 },
        lineColor: [0, 0, 0] as [number, number, number],
        halign: 'left' as const,
        valign: 'middle' as const
      },
      bodyStyles: {
        lineWidth: { top: 0, right: 0.01, bottom: 0.01, left: 0.01 }
      },
      margin: { left: margin, right: margin },
      tableLineColor: [0, 0, 0] as [number, number, number],
      tableLineWidth: 0.01
    };

    // SUMMARY - Use Object Description if no Object IDs provided
    const objectsList = data.objects && data.objects.length > 0 && data.objects.some(obj => obj.trim())
      ? data.objects.filter(obj => obj.trim()).map(obj => `- ${obj}`).join('\n')
      : `- ${data.objectDescription}`;

    let summaryText = `STM has performed MT of following ID(s):\n\n${objectsList}`;
    
    if (data.comments && data.comments.trim()) {
      summaryText += `\n\nCOMMENTS:\n\n${data.comments}`;
    }

    autoTable(doc, {
      startY: yPos,
      head: [['SUMMARY']],
      body: [[summaryText]],
      ...summaryTableOptions,
      bodyStyles: {
        fontSize: 8,
        minCellHeight: summaryHeight
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // RESULT — same shape/chrome as ET (taller bar, white left-aligned text)
    const isRejected = data.status === 'Not Accepted'
    autoTable(doc, {
      startY: yPos,
      head: [['RESULT']],
      body: [[isRejected ? 'REJECTED' : 'ACCEPTED']],
      ...getNdtResultTableOptions(margin),
      bodyStyles: getNdtResultBodyStyles(isRejected),
    });
    
    yPos = (doc as any).lastAutoTable.finalY;
    
    // If approved, show both ISSUED BY and VERIFIED BY sections
    // If not approved, only show ISSUED BY section (full width)
    const isApproved = Boolean(verifierName);
    const signatureWidth = isApproved ? (pageWidth - (2 * margin)) / 2 : (pageWidth - (2 * margin));
    
    // Draw signature boxes with borders
    doc.setDrawColor(0);
    doc.setLineWidth(0.1);
    
    // Draw signature boxes
    doc.rect(margin, yPos, signatureWidth, 25);
    if (isApproved) {
      doc.rect(margin + signatureWidth, yPos, signatureWidth, 25);
    }
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    
    // ISSUED BY (always shown)
    doc.text('ISSUED BY', margin + (signatureWidth / 2), yPos + 4, { align: 'center' });
    
    // VERIFIED BY (only shown if approved)
    if (isApproved) {
      doc.text('VERIFIED BY', margin + signatureWidth + (signatureWidth / 2), yPos + 4, { align: 'center' });
    }
    
    // Add inspector signature
    if (inspectorSignature && typeof inspectorSignature === 'string') {
      doc.addImage(inspectorSignature as string, 'PNG', margin + (signatureWidth/2) - 20, yPos + 8, 40, 10);
    }
    
    // Add approver signature if available
    if (approverSignature && typeof approverSignature === 'string' && isApproved) {
      doc.addImage(approverSignature as string, 'PNG', margin + signatureWidth + (signatureWidth/2) - 20, yPos + 8, 40, 10);
    }
    
    // Inspector name and date
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const signatureSite = data.testSite || 'Bergen'
    doc.text(`${getFullName(data.issuedBy)} / ${formatDateDDMMYYYY(data.testDate)} / ${signatureSite}`, 
             margin + (signatureWidth / 2), yPos + 22, { align: 'center' });
    
    // Approver name and date (only if approved)
    if (isApproved && verifierName) {
      doc.text(`${getFullName(verifierName)} / ${formatDateDDMMYYYY(data.approvedAt || data.testDate)} / ${signatureSite}`, 
               margin + signatureWidth + (signatureWidth / 2), yPos + 22, { align: 'center' });
    }
    
    // Add image page if image is attached
    if (data.attachedImage) {
      doc.addPage();
      
      // Add header with title and logo on second page (styled like main header)
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 58, 95); // #1E3A5F - navy blue
      doc.text('Magnetic Particle', margin, margin);
      const imgHeaderWidth = doc.getTextWidth('Magnetic Particle');
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(65, 65, 65); // black/dark gray
      doc.text(' Testing - Attached Image', margin + imgHeaderWidth, margin);
      
      // Add logo on second page
      if (logoDataUrl && typeof logoDataUrl === 'string') {
        doc.addImage(logoDataUrl as string, 'PNG', pageWidth - margin - 40, margin - 5, 40, 12);
      }
      
      // Add outer border
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.rect(margin, margin + 10, pageWidth - 2 * margin, pageHeight - 2 * margin - 10);
      
      // Calculate image dimensions to fit within the bordered area
      const availableWidth = pageWidth - 4 * margin; // Leave extra margin inside border
      const availableHeight = pageHeight - 4 * margin - 20; // Leave space for header
      
      // Add image within the border
      try {
        const imgWidth = availableWidth * 0.8; // Use 80% of available width
        const imgHeight = availableHeight * 0.8; // Use 80% of available height
        const imgX = margin + (availableWidth - imgWidth) / 2 + margin; // Center horizontally
        const imgY = margin + 20 + (availableHeight - imgHeight) / 2 + margin; // Center vertically
        
        doc.addImage(data.attachedImage, 'JPEG', imgX, imgY, imgWidth, imgHeight);
      } catch (error) {
        console.error('Error adding image to PDF:', error);
        // Add error text if image fails to load
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text('Error loading attached image', pageWidth / 2, pageHeight / 2, { align: 'center' });
      }
    }
    
    // Footer
    const pageCount = (doc.internal as any).getNumberOfPages?.() || doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.setFontSize(7);
      doc.text('STM Inspection AS. NO 997 553 608 MVA. Phone: +47 55 94 30 90. Web: stmi.no email: inspection@stmas.no', 
                pageWidth / 2, pageHeight - 5, { align: 'center' });
    }
    
    // If MT certificate PDF exists, merge it with the generated report
    if (mtCertificatePDF) {
      console.log('Merging MT certificate with report...');
      try {
        // Convert jsPDF to ArrayBuffer
        const reportArrayBuffer = doc.output('arraybuffer');
        
        // Load both PDFs with pdf-lib
        const [reportPdfDoc, certificatePdfDoc] = await Promise.all([
          PDFDocument.load(reportArrayBuffer),
          PDFDocument.load(mtCertificatePDF)
        ]);
        
        // Create a new merged PDF
        const mergedPdf = await PDFDocument.create();
        
        // Copy all pages from the report
        const reportPages = await mergedPdf.copyPages(reportPdfDoc, reportPdfDoc.getPageIndices());
        reportPages.forEach((page: PDFPage) => mergedPdf.addPage(page));
        
        // Copy all pages from the certificate (append at the end)
        const certificatePages = await mergedPdf.copyPages(certificatePdfDoc, certificatePdfDoc.getPageIndices());
        certificatePages.forEach((page: PDFPage) => mergedPdf.addPage(page));
        
        // Save the merged PDF
        const mergedPdfBytes = await mergedPdf.save();
        const mergedBlob = new Blob([new Uint8Array(mergedPdfBytes)], { type: 'application/pdf' });
        
        console.log('MT certificate successfully merged with report');
        return mergedBlob;
      } catch (mergeError) {
        console.error('Error merging MT certificate with report:', mergeError);
        // If merging fails, return the original report without certificate
        const pdfBlob = doc.output('blob');
        return pdfBlob;
      }
    }
    
    // Convert to blob and return (if no certificate to merge)
    const pdfBlob = doc.output('blob');
    return pdfBlob;
  } catch (error) {
    throw error;
  }
}

export const generateMTReport = generatePDF; 